import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../auth/AuthContext";
import { Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FaUserShield, FaSyncAlt } from "react-icons/fa";
import memoryApi from "../api/memoryApi";

// Platform badge colors — web / ios / mobile-google (+ any exact tag).
const PLATFORM_STYLE = {
  web: { bg: "rgba(53,194,214,0.15)", fg: "var(--color-accent-blue)" },
  ios: { bg: "rgba(120,120,128,0.18)", fg: "var(--color-text-primary)" },
  "mobile-google": { bg: "rgba(76,175,80,0.15)", fg: "var(--color-accent-green)" },
};

const AdminUsers = () => {
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null); // { users, aggregates }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isAdmin = ["admin", "owner"].includes(user?.["custom:membership"]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await memoryApi.getUserAnalytics();
      setData(result);
    } catch (e) {
      setError(e.message || "Failed to load user analytics");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) fetchData();
  }, [isAdmin, fetchData]);

  if (authLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center" style={{ minHeight: "100vh" }}>
        <div className="loader-spinner"></div>
      </div>
    );
  }

  // Block non-admins (route is also gated; this is defense-in-depth).
  if (!isAdmin) return <Navigate to="/" replace />;

  const agg = data?.aggregates;
  const users = data?.users || [];

  const card = (label, value, color) => (
    <div className="card-glass p-6">
      <h3 className="text-sm font-semibold mb-2 text-secondary">{label}</h3>
      <p className="text-3xl font-bold" style={{ color: color || "var(--color-accent-blue)" }}>{value}</p>
    </div>
  );

  const platformBadge = (p) => {
    const s = PLATFORM_STYLE[p] || { bg: "rgba(128,128,128,0.15)", fg: "var(--color-text-secondary)" };
    return (
      <span className="px-2 py-1 rounded text-xs font-semibold" style={{ backgroundColor: s.bg, color: s.fg }}>
        {p}
      </span>
    );
  };

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

  return (
    <>
      <Helmet>
        <title>User Analytics - LimKC</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="w-full h-full overflow-auto">
        <section className="section" style={{ paddingTop: "calc(var(--navbar-height) + 2rem)" }}>
          <div className="section-container">
            <div className="flex items-center justify-between mb-8">
              <div className="section-header" style={{ margin: 0 }}>
                <h2 className="section-title flex items-center gap-2"><FaUserShield /> Registered Users</h2>
                <p className="section-description">Signups by platform, membership, credits, and engagement.</p>
              </div>
              <button
                onClick={fetchData}
                disabled={loading}
                className="px-4 py-2 rounded-lg font-semibold flex items-center gap-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg disabled:opacity-60"
              >
                <FaSyncAlt className={loading ? "animate-spin" : ""} /> Refresh
              </button>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="loader-spinner mx-auto mb-4"></div>
                <p className="text-secondary">Loading users...</p>
              </div>
            ) : error ? (
              <div className="card-glass p-6 text-center">
                <p style={{ color: "var(--color-accent-orange)" }}>{error}</p>
              </div>
            ) : agg ? (
              <>
                {/* Aggregate cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
                  {card("Total Users", agg.total, "var(--color-accent-blue)")}
                  {card("Chatted", agg.chatted, "var(--color-accent-green)")}
                  {card("Signup-only", agg.signup_only, "var(--color-accent-orange)")}
                  {card("Credits Outstanding", agg.credits_outstanding, "var(--color-accent-purple)")}
                </div>

                {/* By platform + by tier */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="card-glass p-6">
                    <h3 className="text-lg font-semibold mb-3">By Platform</h3>
                    <div className="flex flex-wrap gap-3">
                      {Object.entries(agg.by_platform || {}).sort(([, a], [, b]) => b - a).map(([p, n]) => (
                        <div key={p} className="flex items-center gap-2">{platformBadge(p)}<span className="font-bold">{n}</span></div>
                      ))}
                    </div>
                  </div>
                  <div className="card-glass p-6">
                    <h3 className="text-lg font-semibold mb-3">By Membership</h3>
                    <div className="flex flex-wrap gap-3">
                      {Object.entries(agg.by_tier || {}).sort(([, a], [, b]) => b - a).map(([t, n]) => (
                        <div key={t} className="flex items-center gap-2">
                          <span className="px-2 py-1 rounded text-xs font-semibold" style={{ backgroundColor: "rgba(128,128,128,0.15)", color: "var(--color-text-primary)" }}>{t}</span>
                          <span className="font-bold">{n}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Per-user table */}
                <div className="card-glass p-6">
                  <h3 className="text-xl font-bold mb-4">All Users ({users.length})</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b" style={{ borderColor: "var(--color-border)" }}>
                          <th className="text-left py-3 px-3">User</th>
                          <th className="text-left py-3 px-3">Platform</th>
                          <th className="text-left py-3 px-3">Tier</th>
                          <th className="text-left py-3 px-3">Provider</th>
                          <th className="text-left py-3 px-3">Member since</th>
                          <th className="text-right py-3 px-3">Credits</th>
                          <th className="text-right py-3 px-3">Sessions</th>
                          <th className="text-right py-3 px-3">Msgs</th>
                          <th className="text-right py-3 px-3">Turns</th>
                          <th className="text-right py-3 px-3">Images</th>
                          <th className="text-right py-3 px-3">Artifacts</th>
                          <th className="text-left py-3 px-3">Last active</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((u, i) => (
                          <tr key={u.email || i} className="border-b" style={{ borderColor: "var(--color-border)" }}>
                            <td className="py-3 px-3">
                              <div className="font-semibold">{u.name || "—"}</div>
                              <div className="text-secondary text-xs">{u.email}</div>
                            </td>
                            <td className="py-3 px-3">{platformBadge(u.platform)}</td>
                            <td className="py-3 px-3">{u.tier}</td>
                            <td className="py-3 px-3">{u.provider}</td>
                            <td className="py-3 px-3">{fmtDate(u.member_since || u.created_at)}</td>
                            <td className="text-right py-3 px-3">{u.credits_balance}</td>
                            <td className="text-right py-3 px-3">{u.sessions}</td>
                            <td className="text-right py-3 px-3">{u.messages}</td>
                            <td className="text-right py-3 px-3">{u.lifetime_turns ?? 0}</td>
                            <td className="text-right py-3 px-3">{u.lifetime_images ?? 0}</td>
                            <td className="text-right py-3 px-3">{u.lifetime_artifacts ?? 0}</td>
                            <td className="py-3 px-3 text-secondary">{u.last_active ? new Date(u.last_active).toLocaleString() : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-12"><p className="text-secondary">No data</p></div>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default AdminUsers;